import { useRouter } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useOptionalProject } from "@/features/project/project-context"

/** The app path of an agent's page (`?tab=` when not Now). */
export const agentPagePath = (workspaceId: string, projectId: string, handle: string, tab?: string): string =>
  `/w/${encodeURIComponent(workspaceId)}/p/${encodeURIComponent(projectId)}/agents/${encodeURIComponent(handle)}${tab && tab !== "now" ? `?tab=${tab}` : ""}`

/** The app path of a memory's page (`/m/M-7`). */
export const memoryPagePath = (workspaceId: string, projectId: string, ref: string): string =>
  `/w/${encodeURIComponent(workspaceId)}/p/${encodeURIComponent(projectId)}/m/${encodeURIComponent(ref)}`

const plainClick = (event: React.MouseEvent) =>
  !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)

/**
 * Wave C (decision 0071, spec §3.1): every agent name, chip and @mention opens her page. A real anchor routed in place
 * on a plain click (no reload, the chat socket stays), like the Markdown's internal links; it also renders where no
 * router is mounted (static renders of the chat), as a plain link. Outside a project it is just the text.
 */
export function AgentLink({
  handle,
  tab,
  children,
  className,
  title,
}: {
  handle: string
  tab?: string | undefined
  children: ReactNode
  className?: string | undefined
  title?: string | undefined
}) {
  const ctx = useOptionalProject()
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | undefined
  if (!ctx) return <span className={className}>{children}</span>
  const href = agentPagePath(ctx.workspaceId, ctx.projectId, handle, tab)
  return (
    <a
      href={href}
      title={title}
      className={className}
      onClick={(event) => {
        if (!router || !plainClick(event)) return
        event.preventDefault()
        router.history.push(href)
      }}
    >
      {children}
    </a>
  )
}
